import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Chat as ChatIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  StarRounded as StarRoundedIcon,
  PlayArrowRounded as PlayArrowRoundedIcon,
} from '@mui/icons-material';
import { useAuth } from '../App';
import { FavoritePrompt } from '../services/chatService';

interface Conversation {
  id: string;
  title: string;
  timestamp: Date;
  messages?: { content: string }[];
}

interface LeftSidebarProps {
  conversations: Conversation[];
  onNewChat: () => void;
  onSelectConversation: (conversationId: string) => void;
  currentConversationId?: string | null;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onRenameConversation?: (conversationId: string, newTitle: string) => void;
  onDeleteConversation?: (conversationId: string) => void;
  favoritePrompts?: FavoritePrompt[];
  onUseFavoritePrompt?: (promptText: string) => void;
  onDeleteFavoritePrompt?: (promptId: string) => void;
  isLoading?: boolean;
}

const LeftSidebar: React.FC<LeftSidebarProps> = ({
  conversations,
  onNewChat,
  onSelectConversation,
  currentConversationId,
  isCollapsed,
  onToggleCollapse,
  onRenameConversation,
  onDeleteConversation,
  favoritePrompts = [],
  onUseFavoritePrompt,
  onDeleteFavoritePrompt,
  isLoading = false,
}) => {
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    conversationId: string;
  } | null>(null);
  const [renameDialog, setRenameDialog] = useState<{
    open: boolean;
    conversationId: string;
    currentTitle: string;
    newTitle: string;
  }>({
    open: false,
    conversationId: '',
    currentTitle: '',
    newTitle: '',
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    conversationId: string;
    title: string;
  }>({
    open: false,
    conversationId: '',
    title: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingSearchFocus, setPendingSearchFocus] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isCollapsed && pendingSearchFocus) {
      // Small delay to let the TextField render after sidebar expands
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
        setPendingSearchFocus(false);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isCollapsed, pendingSearchFocus]);

  const handleSearchIconClick = () => {
    onToggleCollapse();
    setPendingSearchFocus(true);
  };

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter(c =>
      c.title.toLowerCase().includes(q) ||
      c.messages?.some(m => m.content.toLowerCase().includes(q))
    );
  }, [conversations, searchQuery]);

  const truncateTitle = (title: string, maxLength: number = 25) => {
    return title.length > maxLength ? `${title.substring(0, maxLength)}...` : title;
  };

  const handleContextMenu = (event: React.MouseEvent, conversationId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      conversationId,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleRename = (conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setRenameDialog({
        open: true,
        conversationId,
        currentTitle: conversation.title,
        newTitle: conversation.title,
      });
    }
    handleCloseContextMenu();
  };

  const handleDelete = (conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setDeleteDialog({
        open: true,
        conversationId,
        title: conversation.title,
      });
    }
    handleCloseContextMenu();
  };

  const handleRenameConfirm = () => {
    if (onRenameConversation && renameDialog.newTitle.trim()) {
      onRenameConversation(renameDialog.conversationId, renameDialog.newTitle.trim());
    }
    setRenameDialog({ open: false, conversationId: '', currentTitle: '', newTitle: '' });
  };

  const handleDeleteConfirm = () => {
    if (onDeleteConversation) {
      onDeleteConversation(deleteDialog.conversationId);
    }
    setDeleteDialog({ open: false, conversationId: '', title: '' });
  };

  const UserInfo: React.FC = () => {
    const { user, logout } = useAuth();
    const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      setUserMenuAnchor(event.currentTarget);
    };

    const handleUserMenuClose = () => {
      setUserMenuAnchor(null);
    };

    const handleLogout = () => {
      handleUserMenuClose();
      logout();
    };

    if (!user) return null;

    // Extract first and last name from user data
    const fullName = user.first_name && user.last_name 
      ? `${user.first_name} ${user.last_name}`
      : user.username || 'User';
    
    // Create initials from first and last name
    const initials = user.first_name && user.last_name
      ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
      : (user.username ? user.username.charAt(0).toUpperCase() : 'U');

    return (
      <>
        <ListItemButton
          onClick={handleUserMenuOpen}
          aria-label="Open user account menu"
          sx={{
            borderRadius: 1,
            py: 1,
            px: 1,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, mr: 1 }}>
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: '#2563eb', // Claude-like blue color
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            >
              {initials}
            </Avatar>
          </ListItemIcon>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'text.primary',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {fullName}
            </Typography>
          </Box>
        </ListItemButton>

        {/* Custom positioned dropdown */}
        {Boolean(userMenuAnchor) && (
          <>
            {/* Backdrop to close dropdown when clicking outside */}
            <Box
              onClick={handleUserMenuClose}
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999,
              }}
            />
            {/* Profile dropdown */}
            <Box
              className="profile-dropdown"
              sx={{
                position: 'absolute',
                right: 12,
                bottom: 76, // Increased from 64px to 76px to create 12px margin from avatar
                width: 'calc(100% - 24px)', // keeps it inside 240px sidebar with padding
                backgroundColor: 'background.paper',
                borderRadius: 0.75, // Subtle rounding
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                overflow: 'hidden',
              }}
            >
              {/* User Info Header */}
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar 
                    sx={{ 
                      width: 24, 
                      height: 24, 
                      bgcolor: '#2563eb',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: 'text.primary',
                        lineHeight: 1.2,
                      }}
                    >
                      {fullName}
                    </Typography>
                    {user.email && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          lineHeight: 1.2,
                        }}
                      >
                        {user.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              
              {/* Logout Button */}
              <Box sx={{ p: 1 }}>
                <ListItemButton
                  onClick={handleLogout}
                  sx={{
                    borderRadius: 0.5,
                    py: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <ListItemText 
                    primary="Log out"
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </Box>
            </Box>
          </>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        width: isCollapsed ? 60 : 240,
        height: '100vh',
        flexShrink: 0,
        backgroundColor: 'action.hover',
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease-in-out',
        overflow: 'hidden',
        position: 'relative', // Enable absolute positioning for dropdown
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
        }}
      >
        <IconButton
          onClick={onToggleCollapse}
          sx={{
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <MenuIcon />
        </IconButton>
        
        {!isCollapsed && (
          <Box
            component="img"
            src="/otti-headline-logo.png"
            alt="Otti"
            sx={{
              height: 33.6, // 32 * 1.05 = 33.6
              maxWidth: 157.5, // 150 * 1.05 = 157.5
              objectFit: 'contain',
            }}
          />
        )}
      </Box>

      {/* New Chat Button */}
      <Box sx={{ p: 2 }}>
        <ListItemButton
          onClick={onNewChat}
          disabled={isLoading}
          sx={{
            borderRadius: 1,
            py: 1.5,
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            opacity: isLoading ? 0.5 : 1,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            '&:hover': {
              backgroundColor: isLoading ? 'transparent' : 'primary.light',
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 36 }}>
            <AddIcon sx={{ color: isLoading ? 'text.disabled' : 'primary.main' }} />
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText
              primary="New chat"
              primaryTypographyProps={{
                fontSize: '0.95rem',
                fontWeight: 500,
                color: isLoading ? 'text.disabled' : 'primary.main',
              }}
            />
          )}
        </ListItemButton>
      </Box>

      {/* Search Icon (collapsed state) */}
      {isCollapsed && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
          <IconButton
            onClick={handleSearchIconClick}
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <SearchIcon />
          </IconButton>
        </Box>
      )}

      {/* Search Field */}
      {!isCollapsed && (
        <Box sx={{ px: 2, pb: 1 }}>
          <TextField
            size="small"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.85rem',
                borderRadius: 1,
                backgroundColor: 'background.paper',
              },
              '& .MuiOutlinedInput-input': {
                py: 0.75,
              },
            }}
          />
        </Box>
      )}

      {/* Favorite Prompts */}
      {!isCollapsed && favoritePrompts.length > 0 && (
        <>
          <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <StarRoundedIcon sx={{ fontSize: '1rem', color: '#f59e0b' }} />
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              Favorites
            </Typography>
          </Box>
          <Divider />
          {/* Capped at 25 server-side, but scrollable so a long list never
              pushes the conversation list off screen. */}
          <Box sx={{ maxHeight: 200, overflow: 'auto', flexShrink: 0 }}>
            <List dense sx={{ px: 1 }}>
              {favoritePrompts.map((prompt) => (
                <ListItem key={prompt.id} sx={{ px: 1, py: 0.25 }}>
                  <ListItemButton
                    onClick={() => !isLoading && onUseFavoritePrompt && onUseFavoritePrompt(prompt.prompt_text)}
                    disabled={isLoading}
                    title={prompt.prompt_text}
                    sx={{
                      borderRadius: 1,
                      py: 0.75,
                      pr: 7,
                      position: 'relative',
                      alignItems: 'flex-start',
                      // Reveal Run/Remove on hover or keyboard focus of the row.
                      '&:hover .favorite-prompt-actions, &:focus-within .favorite-prompt-actions': {
                        opacity: 1,
                      },
                    }}
                  >
                    {/* Show the full saved question (2 lines) rather than a
                        truncated auto-name, so the user can read what a favorite
                        will run. The complete text stays on hover via the row
                        `title`. */}
                    <ListItemText
                      primary={prompt.prompt_text}
                      primaryTypographyProps={{
                        sx: {
                          fontSize: '0.85rem',
                          lineHeight: 1.35,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        },
                      }}
                    />
                    <Box
                      className="favorite-prompt-actions"
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        opacity: 0,
                        transition: 'opacity 0.15s ease',
                      }}
                    >
                      <IconButton
                        component="div"
                        role="button"
                        size="small"
                        aria-label="Run this favorite"
                        title="Run"
                        onClick={(e) => {
                          // The row already sends the prompt; this is the explicit
                          // affordance. Stop bubbling so it fires exactly once.
                          e.stopPropagation();
                          if (!isLoading && onUseFavoritePrompt) onUseFavoritePrompt(prompt.prompt_text);
                        }}
                        sx={{ p: 0.25 }}
                      >
                        <PlayArrowRoundedIcon sx={{ fontSize: '1.15rem', color: 'primary.main' }} />
                      </IconButton>
                      <IconButton
                        component="div"
                        role="button"
                        size="small"
                        aria-label="Remove from favorites"
                        title="Remove from favorites"
                        onClick={(e) => {
                          // The row itself sends the prompt, so this must not bubble.
                          e.stopPropagation();
                          if (onDeleteFavoritePrompt) onDeleteFavoritePrompt(prompt.id);
                        }}
                        sx={{ p: 0.25 }}
                      >
                        <CloseIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </>
      )}

      {/* Recent Conversations */}
      {!isCollapsed && (
        <>
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              Recent
            </Typography>
          </Box>
          <Divider />
        </>
      )}

      {/* Conversation List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List dense sx={{ px: 1 }}>
          {filteredConversations.map((conversation) => (
            <ListItem key={conversation.id} sx={{ px: 1, py: 0.5 }}>
              <ListItemButton
                onClick={() => !isLoading && onSelectConversation(conversation.id)}
                onContextMenu={(e) => !isLoading && handleContextMenu(e, conversation.id)}
                selected={currentConversationId === conversation.id}
                disabled={isLoading}
                sx={{
                  borderRadius: 1,
                  py: 1,
                  minHeight: 44,
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  position: 'relative',
                  opacity: isLoading ? 0.5 : 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                  '&:hover .conversation-menu-button': {
                    opacity: isLoading ? 0 : 1,
                    visibility: 'visible',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 36 }}>
                  <ChatIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <>
                    <ListItemText
                      primary={truncateTitle(conversation.title)}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        noWrap: true,
                      }}
                    />
                    {/* Three dots menu button */}
                    <IconButton
                      className="conversation-menu-button"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleContextMenu(e, conversation.id);
                      }}
                      sx={{
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'all 0.2s ease',
                        ml: 1,
                        p: 0.5,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    </IconButton>
                  </>
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* User Info & Logout */}
      {!isCollapsed && (
        <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
          <UserInfo />
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: {
            minWidth: 160,
          },
        }}
      >
        <MenuItem onClick={() => contextMenu && handleRename(contextMenu.conversationId)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Rename" />
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleDelete(contextMenu.conversationId)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog
        open={renameDialog.open}
        onClose={() => setRenameDialog({ ...renameDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rename Conversation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Conversation title"
            value={renameDialog.newTitle}
            onChange={(e) => setRenameDialog({ ...renameDialog, newTitle: e.target.value })}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleRenameConfirm();
              }
            }}
            margin="normal"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog({ ...renameDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleRenameConfirm} variant="contained">
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ ...deleteDialog, open: false })}
        maxWidth="sm"
      >
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{truncateTitle(deleteDialog.title, 50)}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ ...deleteDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeftSidebar;